import { NgModule } from '@angular/core';
import { RouterModule, Routes } from '@angular/router';
import { defaultRouteGuard, LoginComponent } from '@card-software/library';
import { MENU_ITEMS } from './app-menu';
import { AuthenticationGuard } from './guards/authentication.guard';
import { RoleGuard } from './guards/role.guard';
import { UserGroup } from './models/authorization/enum/user-group.enum';
import { DispatchInstructionDetailsComponent } from './components/dispatch-instructions/dispatch-instruction-details/dispatch-instruction-details.component';
import { DispatchInstructionsComponent } from './components/dispatch-instructions/dispatch-instructions/dispatch-instructions.component';
import { InventoryAdjustmentDetailsComponent } from './components/inventory-adjustment/inventory-adjustment-details.component';
import { InventoryAdjustmentComponent } from './components/inventory-adjustment/inventory-adjustment.component';
import { InventorySessionDetailsComponent } from './components/inventory-sessions/inventory-session-details/inventory-session-details.component';
import { BalanceSnapshotRunsComponent } from './components/balance-snapshot-runs/balance-snapshot-runs.component';
import { KardexAuditDashboardComponent } from './components/kardex-audit/kardex-audit-dashboard/kardex-audit-dashboard.component';
import { InventorySessionsComponent } from './components/inventory-sessions/inventory-sessions/inventory-sessions.component';
import { InventoryTypeComponent } from './components/inventory-type/inventory-type.component';
import { ItemsInventoryDetailComponent } from './components/items-inventory-detail/items-inventory-detail.component';
import { ItemsInventoryComponent } from './components/items-inventory/items-inventory.component';
import { LocationsComponent } from './components/locations/locations.component';
import { LotItemTransferDetailsComponent } from './components/lot-item-transfers/lot-item-transfer-details/lot-item-transfer-details.component';
import { LotItemTransfersComponent } from './components/lot-item-transfers/lot-item-transfers/lot-item-transfers.component';
import { LotItemRelocationDetailsComponent } from './components/lot-item-relocation/lot-item-relocation-details/lot-item-relocation-details.component';
import { LotItemRelocationComponent } from './components/lot-item-relocation/lot-item-relocation/lot-item-relocation.component';
import { FinishedGoodTransferDetailsComponent } from './components/production-card/finished-good-transfer-details/finished-good-transfer-details.component';
import { FinishedGoodTransfersComponent } from './components/production-card/finished-good-transfers/finished-good-transfers.component';
import { ProductionInventoryComponent } from './components/production-inventory/production-inventory.component';
import { RemissionGuideDetailsComponent } from './components/remission-guides/remission-guide-details/remission-guide-details.component';
import { RemissionGuidesComponent } from './components/remission-guides/remission-guides/remission-guides.component';
import { ReportingComponent } from './components/reporting/reporting.component';
import { RequisitionDetailComponent } from './components/requisition-details/requisition-detail/requisition-detail.component';
import { RequisitionsComponent } from './components/requisition-details/requisitions/requisitions.component';
import { ReturnSurplusComponent } from './components/return-surplus/return-surplus.component';
import { RollQcDetailComponent } from './components/roll-qc/roll-qc-detail/roll-qc-detail.component';
import { SecondQualityDispatchDetailsComponent } from './components/second-quality-dispatches/second-quality-dispatch-details/second-quality-dispatch-details.component';
import { SecondQualityDispatchListComponent } from './components/second-quality-dispatches/second-quality-dispatch-list/second-quality-dispatch-list.component';
import { SplitLotItemsDetailComponent } from './components/split-lot-items/split-lot-items-detail/split-lot-items-detail.component';
import { SupplyRequestDetailComponent } from './components/supply-request/supply-request-detail/supply-request-detail.component';
import { SupplyRequestListComponent } from './components/supply-request/supply-request-list/supply-request-list.component';
import { SupplyWarehouseDetailComponent } from './components/supply-warehouse/supply-warehouse-detail/supply-warehouse-detail.component';
import { SupplyWarehouseListComponent } from './components/supply-warehouse/supply-warehouse-list/supply-warehouse-list.component';
import { TransferUnitDetailsComponent } from './components/transfer-units/transfer-unit-details/transfer-unit-details.component';
import { TransferUnitsComponent } from './components/transfer-units/transfer-units/transfer-units.component';
import { UnauthorizedComponent } from './components/unauthorized/unauthorized.component';
import { WarehouseImportsDetailComponent } from './components/warehouse-inventory/warehouse-imports-details/warehouse-imports-details.component';
import { WarehouseImportsComponent } from './components/warehouse-inventory/warehouse-imports/warehouse-imports.component';
import { YarnQcDetailsComponent } from './components/yarn-qc/yarn-qc-details/yarn-qc-details.component';
import { YarnsQcComponent } from './components/yarn-qc/yarns-qc/yarns-qc.component';
import { YarnRequisitionsDetailsComponent } from './components/yarn-requisitions/yarn-requisitions-details/yarn-requisitions-details.component';
import { YarnRequisitionsComponent } from './components/yarn-requisitions/yarn-requisitions/yarn-requisitions.component';
import { SplitLotItemsListComponent } from './components/split-lot-items/split-lot-items-list/split-lot-items-list.component';
import { MergeLotItemsListComponent } from './components/merge-lot-items/merge-lot-items-list/merge-lot-items-list.component';
import { MergeLotItemsDetailComponent } from './components/merge-lot-items/merge-lot-items-detail/merge-lot-items-detail.component';
import { BusinessLotNumbersComponent } from './components/business-lot-numbers/business-lot-numbers.component';

const routes: Routes = [
  {
    path: 'login',
    component: LoginComponent,
  },
  {
    path: 'warehouse-imports',
    component: WarehouseImportsComponent,
    canActivate: [AuthenticationGuard, RoleGuard],
    data: {
      breadcrumb: [{ label: 'Warehouse_Imports_Title_Generic' }],
      roles: [
        UserGroup.YarnWarehouseImportsEditor,
        UserGroup.YarnWarehouseImportsAdmin,
        UserGroup.YarnWarehouseImportsSupervisor,
        UserGroup.YarnWarehouseImportsViewer,
      ],
    },
  },
  {
    path: 'warehouse-import-details',
    component: WarehouseImportsDetailComponent,
    canActivate: [AuthenticationGuard, RoleGuard],
    data: {
      breadcrumb: [{ label: 'Warehouse_Imports_Title_Generic', route: '/warehouse-imports' }, { label: 'Common_Detail' }],
      roles: [
        UserGroup.YarnWarehouseImportsEditor,
        UserGroup.YarnWarehouseImportsAdmin,
        UserGroup.YarnWarehouseImportsSupervisor,
        UserGroup.YarnWarehouseImportsViewer,
      ],
    },
  },
  {
    path: 'inventory-type',
    component: InventoryTypeComponent,
    canActivate: [AuthenticationGuard, RoleGuard],
    data: {
      breadcrumb: [{ label: 'Inventory_Types_Title' }],
      roles: [UserGroup.InventoryTypesAdmin, UserGroup.InventoryTypesViewer],
    },
  },
  {
    path: 'items-inventory',
    component: ItemsInventoryComponent,
    canActivate: [AuthenticationGuard, RoleGuard],
    data: {
      breadcrumb: [{ label: 'Items_Inventory_Title' }],
      roles: [UserGroup.ItemsInventoryAdmin, UserGroup.ItemsInventoryViewer],
    },
  },
  {
    path: 'items-inventory-detail',
    component: ItemsInventoryDetailComponent,
    canActivate: [AuthenticationGuard, RoleGuard],
    data: {
      breadcrumb: [
        { label: 'Items_Inventory_Title', route: '/items-inventory' },
        { label: 'Common_Detail', awaitCurrent: true },
      ],
      roles: [UserGroup.ItemsInventoryAdmin, UserGroup.ItemsInventoryViewer],
    },
  },
  {
    path: 'supply-request',
    component: SupplyRequestListComponent,
    canActivate: [AuthenticationGuard, RoleGuard],
    data: {
      breadcrumb: [{ label: 'Supply_Requests' }],
      roles: [UserGroup.SupplyRequestViewer, UserGroup.SupplyRequestAdmin],
    },
  },
  {
    path: 'supply-request-detail',
    component: SupplyRequestDetailComponent,
    canActivate: [AuthenticationGuard, RoleGuard],
    data: {
      breadcrumb: [
        { label: 'Supply_Requests', route: '/supply-request' },
        { label: 'Common_Detail', awaitCurrent: true },
      ],
      roles: [UserGroup.SupplyRequestViewer, UserGroup.SupplyRequestAdmin],
    },
  },
  {
    path: 'supply-warehouse',
    component: SupplyWarehouseListComponent,
    canActivate: [AuthenticationGuard, RoleGuard],
    data: {
      breadcrumb: [{ label: 'Supply_Warehouse_Title' }],
      roles: [
        UserGroup.SupplyWarehouseViewer,
        UserGroup.SupplyWarehouseAdmin,
        UserGroup.SupplyWarehouseInternalReturnsAdmin,
        UserGroup.SupplyWarehouseProviderReturnAdmin,
      ],
    },
  },
  {
    path: 'supply-warehouse-detail',
    component: SupplyWarehouseDetailComponent,
    canActivate: [AuthenticationGuard, RoleGuard],
    data: {
      breadcrumb: [{ label: 'Supply_Warehouse_Title', route: '/supply-warehouse' }, { label: 'Common_Detail' }],
      roles: [
        UserGroup.SupplyWarehouseViewer,
        UserGroup.SupplyWarehouseAdmin,
        UserGroup.SupplyWarehouseInternalReturnsAdmin,
        UserGroup.SupplyWarehouseProviderReturnAdmin,
      ],
    },
  },
  {
    path: 'yarn-requisitions',
    component: YarnRequisitionsComponent,
    canActivate: [AuthenticationGuard, RoleGuard],
    data: {
      breadcrumb: [{ label: 'Yarn_Requisitions_Title' }],
      roles: [
        UserGroup.InternalYarnRequisitionAllViewer,
        UserGroup.InternalYarnRequisitionKnittingViewer,
        UserGroup.InternalYarnRequisitionWarehouseViewer,
        UserGroup.InternalYarnRequisitionKnittingEditor,
        UserGroup.InternalYarnRequisitionWarehouseEditor,
        UserGroup.InternalYarnRequisitionAdmin,
      ],
    },
  },
  {
    path: 'yarn-requisition-details',
    component: YarnRequisitionsDetailsComponent,
    canActivate: [AuthenticationGuard, RoleGuard],
    data: {
      breadcrumb: [
        { label: 'Yarn_Requisitions_Title', route: '/yarn-requisitions' },
        { label: 'Common_Detail', awaitCurrent: true },
      ],
      roles: [
        UserGroup.InternalYarnRequisitionAllViewer,
        UserGroup.InternalYarnRequisitionKnittingViewer,
        UserGroup.InternalYarnRequisitionWarehouseViewer,
        UserGroup.InternalYarnRequisitionKnittingEditor,
        UserGroup.InternalYarnRequisitionWarehouseEditor,
        UserGroup.InternalYarnRequisitionAdmin,
      ],
    },
  },
  {
    path: 'transfer-units',
    component: TransferUnitsComponent,
    canActivate: [AuthenticationGuard, RoleGuard],
    data: {
      breadcrumb: [{ label: 'Transfer_Units_Title' }],
      roles: [UserGroup.TransferUnitViewer, UserGroup.TransferUnitEditor, UserGroup.TransferUnitReceiver, UserGroup.TransferUnitAdmin],
    },
  },
  {
    path: 'transfer-unit-details',
    component: TransferUnitDetailsComponent,
    canActivate: [AuthenticationGuard, RoleGuard],
    data: {
      breadcrumb: [
        { label: 'Transfer_Units_Title', route: '/transfer-units' },
        { label: 'Transfer_Units_Details_Title', awaitCurrent: true },
      ],
      roles: [UserGroup.TransferUnitViewer, UserGroup.TransferUnitEditor, UserGroup.TransferUnitReceiver, UserGroup.TransferUnitAdmin],
    },
  },
  {
    path: 'split-lot-items',
    component: SplitLotItemsListComponent,
    canActivate: [AuthenticationGuard, RoleGuard],
    data: {
      breadcrumb: [{ label: 'Split_Lot_Items_Title' }],
      roles: [UserGroup.SplitLotItemViewer, UserGroup.SplitLotItemAdmin],
    },
  },
  {
    path: 'split-lot-items-details',
    component: SplitLotItemsDetailComponent,
    canActivate: [AuthenticationGuard, RoleGuard],
    data: {
      breadcrumb: [{ label: 'Split_Lot_Items_Title', route: '/split-lot-items' }, { label: 'Common_Detail' }],
      roles: [UserGroup.SplitLotItemViewer, UserGroup.SplitLotItemAdmin],
    },
  },
  {
    path: 'merge-lot-items',
    component: MergeLotItemsListComponent,
    canActivate: [AuthenticationGuard, RoleGuard],
    data: {
      breadcrumb: [{ label: 'Merge_Lot_Items_Title' }],
      roles: [UserGroup.MergeLotItemViewer, UserGroup.MergeLotItemAdmin],
    },
  },
  {
    path: 'merge-lot-items-details',
    component: MergeLotItemsDetailComponent,
    canActivate: [AuthenticationGuard, RoleGuard],
    data: {
      breadcrumb: [{ label: 'Merge_Lot_Items_Title', route: '/merge-lot-items' }, { label: 'Common_Detail' }],
      roles: [UserGroup.MergeLotItemViewer, UserGroup.MergeLotItemAdmin],
    },
  },
  {
    path: 'rolls-qc',
    component: RollQcDetailComponent,
    canActivate: [AuthenticationGuard, RoleGuard],
    data: {
      breadcrumb: [{ label: 'RollQcDetail_Title' }],
      roles: [UserGroup.RollQCAdmin, UserGroup.RollQCEditor, UserGroup.RollQCViewer],
    },
  },
  {
    path: 'yarns-qc',
    component: YarnsQcComponent,
    canActivate: [AuthenticationGuard, RoleGuard],
    data: {
      breadcrumb: [{ label: 'YarnsQc_Title' }],
      roles: [UserGroup.YarnQCViewer, UserGroup.YarnQCAdmin],
    },
  },
  {
    path: 'yarn-qc-details',
    component: YarnQcDetailsComponent,
    canActivate: [AuthenticationGuard, RoleGuard],
    data: {
      breadcrumb: [
        { label: 'YarnsQc_Title', route: '/yarns-qc' },
        { label: 'Common_Detail', awaitCurrent: true },
      ],
      roles: [UserGroup.YarnQCViewer, UserGroup.YarnQCAdmin],
    },
  },
  {
    path: 'unauthorized',
    component: UnauthorizedComponent,
  },
  {
    path: 'return-surplus',
    component: ReturnSurplusComponent,
    canActivate: [AuthenticationGuard, RoleGuard],
    data: {
      breadcrumb: [{ label: 'Return_Surplus_Title' }],
      roles: [UserGroup.ReturnSurplusViewer, UserGroup.ReturnSurplusAdmin],
    },
  },
  {
    path: 'reporting',
    component: ReportingComponent,
    canActivate: [AuthenticationGuard, RoleGuard],
    data: {
      breadcrumb: [{ label: 'Reporting_Page_Title' }],
      roles: [UserGroup.InventoryReportingViewer, UserGroup.InventoryReportingAdmin],
    },
  },
  {
    path: 'balance-snapshot-runs',
    component: BalanceSnapshotRunsComponent,
    canActivate: [AuthenticationGuard, RoleGuard],
    data: {
      roles: [UserGroup.InventoryBalanceAdmin, UserGroup.InventoryBalanceViewer],
    },
  },
  {
    path: 'inventory-adjustment',
    component: InventoryAdjustmentComponent,
    canActivate: [AuthenticationGuard, RoleGuard],
    data: {
      breadcrumb: [{ label: 'Inventory_Adjustments_Title' }],
      roles: [UserGroup.InventoryAdjustmentAdmin],
    },
  },
  {
    path: 'inventory-adjustment-details',
    component: InventoryAdjustmentDetailsComponent,
    canActivate: [AuthenticationGuard, RoleGuard],
    data: {
      breadcrumb: [
        { label: 'Inventory_Adjustments_Title', route: '/inventory-adjustment' },
        { label: 'Common_Detail', awaitCurrent: true },
      ],
      roles: [UserGroup.InventoryAdjustmentViewer, UserGroup.InventoryAdjustmentEditor, UserGroup.InventoryAdjustmentAdmin],
    },
  },
  {
    path: 'locations',
    component: LocationsComponent,
    canActivate: [AuthenticationGuard, RoleGuard],
    data: {
      breadcrumb: [{ label: 'Locations_Title' }],
      roles: [UserGroup.SupplyWarehouseAdmin],
    },
  },
  {
    path: 'dispatch-instructions',
    component: DispatchInstructionsComponent,
    canActivate: [AuthenticationGuard, RoleGuard],
    data: {
      breadcrumb: [{ label: 'Dispatch_Instructions_Title' }],
      roles: [
        UserGroup.DispatchInstructionViewer,
        UserGroup.DispatchInstructionCustomerServiceEditor,
        UserGroup.DispatchInstructionExportEditor,
        UserGroup.DispatchInstructionAdmin,
      ],
    },
  },
  {
    path: 'dispatch-instruction-details',
    component: DispatchInstructionDetailsComponent,
    canActivate: [AuthenticationGuard, RoleGuard],
    data: {
      breadcrumb: [
        { label: 'Dispatch_Instructions_Title', route: '/dispatch-instructions' },
        { label: 'Common_Detail', awaitCurrent: true },
      ],
      roles: [
        UserGroup.DispatchInstructionViewer,
        UserGroup.DispatchInstructionCustomerServiceEditor,
        UserGroup.DispatchInstructionExportEditor,
        UserGroup.DispatchInstructionAdmin,
      ],
    },
  },
  {
    path: 'finished-good-transfers',
    component: FinishedGoodTransfersComponent,
    canActivate: [AuthenticationGuard, RoleGuard],
    data: {
      breadcrumb: [{ label: 'Finished_Goods_Transfers_Title' }],
      roles: [
        UserGroup.FinishedGoodsTransferViewer,
        UserGroup.FinishedGoodsTransferEditor,
        UserGroup.FinishedGoodsTransferReceiver,
        UserGroup.FinishedGoodsTransferAdmin,
      ],
    },
  },
  {
    path: 'finished-good-transfer-details',
    component: FinishedGoodTransferDetailsComponent,
    canActivate: [AuthenticationGuard, RoleGuard],
    data: {
      breadcrumb: [
        { label: 'Finished_Goods_Transfers_Title', route: '/finished-good-transfers' },
        { label: 'Finished_Goods_Transfer_Details_Title', awaitCurrent: true },
      ],
      roles: [
        UserGroup.FinishedGoodsTransferViewer,
        UserGroup.FinishedGoodsTransferEditor,
        UserGroup.FinishedGoodsTransferReceiver,
        UserGroup.FinishedGoodsTransferAdmin,
      ],
    },
  },
  {
    path: 'lot-item-transfers',
    component: LotItemTransfersComponent,
    canActivate: [AuthenticationGuard, RoleGuard],
    data: {
      breadcrumb: [{ label: 'Lot_Item_Transfers_Title' }],
      roles: [
        UserGroup.LotItemTransferViewer,
        UserGroup.LotItemTransferEditor,
        UserGroup.LotItemTransferReceiver,
        UserGroup.LotItemTransferAdmin,
        UserGroup.FinishedGoodsRelocationViewer,
        UserGroup.FinishedGoodsRelocationAdmin,
        UserGroup.RawMaterialRelocationViewer,
        UserGroup.RawMaterialRelocationAdmin,
        UserGroup.GreigeSampleSender,
        UserGroup.GreigeSampleReceiver,
        UserGroup.FinishedSampleSender,
        UserGroup.FinishedSampleReceiver,
      ],
    },
  },
  {
    path: 'lot-item-transfer-details',
    component: LotItemTransferDetailsComponent,
    canActivate: [AuthenticationGuard, RoleGuard],
    data: {
      breadcrumb: [
        { label: 'Lot_Item_Transfers_Title', route: '/lot-item-transfers' },
        { label: 'Lot_Item_Transfer_Details_Title', awaitCurrent: true },
      ],
      roles: [
        UserGroup.LotItemTransferViewer,
        UserGroup.LotItemTransferEditor,
        UserGroup.LotItemTransferReceiver,
        UserGroup.LotItemTransferAdmin,
        UserGroup.FinishedGoodsRelocationViewer,
        UserGroup.FinishedGoodsRelocationAdmin,
        UserGroup.RawMaterialRelocationViewer,
        UserGroup.RawMaterialRelocationAdmin,
        UserGroup.GreigeSampleSender,
        UserGroup.GreigeSampleReceiver,
        UserGroup.FinishedSampleSender,
        UserGroup.FinishedSampleReceiver,
      ],
    },
  },
  {
    path: 'lot-item-relocation',
    component: LotItemRelocationComponent,
    canActivate: [AuthenticationGuard, RoleGuard],
    data: {
      breadcrumb: [
        { label: 'Lot_Item_Relocation_Title', route: '/lot-item-relocation-details' },
        { label: 'Lot_Item_Relocation_Tab_History' },
      ],
      roles: [UserGroup.LotItemRelocationViewer, UserGroup.LotItemRelocationAdmin],
    },
  },
  {
    path: 'lot-item-relocation-details',
    component: LotItemRelocationDetailsComponent,
    canActivate: [AuthenticationGuard, RoleGuard],
    data: {
      breadcrumb: [
        { label: 'Lot_Item_Relocation_Title', route: '/lot-item-relocation-details' },
        { label: 'Common_Detail', awaitCurrent: true },
      ],
      roles: [UserGroup.LotItemRelocationViewer, UserGroup.LotItemRelocationAdmin],
    },
  },
  {
    path: 'remission-guides',
    component: RemissionGuidesComponent,
    canActivate: [AuthenticationGuard, RoleGuard],
    data: {
      breadcrumb: [{ label: 'Remission_Guides' }],
      roles: [UserGroup.RemissionGuideViewer, UserGroup.RemissionGuideEditor, UserGroup.RemissionGuideAdmin],
    },
  },
  {
    path: 'remission-guide-details',
    component: RemissionGuideDetailsComponent,
    canActivate: [AuthenticationGuard, RoleGuard],
    data: {
      breadcrumb: [
        { label: 'Remission_Guides', route: '/remission-guides' },
        { label: 'Common_Detail', awaitCurrent: true },
      ],
      roles: [UserGroup.RemissionGuideViewer, UserGroup.RemissionGuideEditor, UserGroup.RemissionGuideAdmin],
    },
  },
  {
    path: 'production-inventory',
    component: ProductionInventoryComponent,
    canActivate: [AuthenticationGuard, RoleGuard],
    data: {
      breadcrumb: [{ label: 'Production_Inventory_Title' }],
      roles: [UserGroup.ProductionInventoryViewer, UserGroup.ProductionInventoryAdmin],
    },
  },
  {
    path: 'requisitions',
    component: RequisitionsComponent,
    canActivate: [AuthenticationGuard, RoleGuard],
    data: {
      breadcrumb: [{ label: 'Requisitions_Title' }],
      roles: [UserGroup.RequisitionViewer, UserGroup.RequisitionEditor, UserGroup.RequisitionAdmin],
    },
  },
  {
    path: 'requisition-details',
    component: RequisitionDetailComponent,
    canActivate: [AuthenticationGuard, RoleGuard],
    data: {
      breadcrumb: [
        { label: 'Requisitions_Title', route: '/requisitions' },
        { label: 'New_Requisition', awaitCurrent: true },
      ],
      roles: [UserGroup.RequisitionViewer, UserGroup.RequisitionEditor, UserGroup.RequisitionAdmin],
    },
  },
  {
    path: 'second-quality-dispatches',
    component: SecondQualityDispatchListComponent,
    canActivate: [AuthenticationGuard, RoleGuard],
    data: {
      breadcrumb: [{ label: 'Second_Quality_Dispatches' }],
      roles: [UserGroup.SecondQualityDispatchViewer, UserGroup.SecondQualityDispatchEditor, UserGroup.SecondQualityDispatchAdmin],
    },
  },
  {
    path: 'inventory-sessions',
    component: InventorySessionsComponent,
    canActivate: [AuthenticationGuard, RoleGuard],
    data: {
      breadcrumb: [{ label: 'Inventory_Sessions' }],
      roles: [UserGroup.InventorySessionViewer, UserGroup.InventorySessionEditor, UserGroup.InventorySessionAdmin],
    },
  },
  {
    path: 'second-quality-dispatch-details',
    component: SecondQualityDispatchDetailsComponent,
    canActivate: [AuthenticationGuard, RoleGuard],
    data: {
      breadcrumb: [
        { label: 'Second_Quality_Dispatches', route: '/second-quality-dispatches' },
        { label: 'Common_Detail', awaitCurrent: true },
      ],
      roles: [UserGroup.SecondQualityDispatchViewer, UserGroup.SecondQualityDispatchEditor, UserGroup.SecondQualityDispatchAdmin],
    },
  },
  {
    path: 'inventory-session-details',
    component: InventorySessionDetailsComponent,
    canActivate: [AuthenticationGuard, RoleGuard],
    data: {
      breadcrumb: [
        { label: 'Inventory_Sessions', route: '/inventory-sessions' },
        { label: 'Common_Detail', awaitCurrent: true },
      ],
      roles: [UserGroup.InventorySessionViewer, UserGroup.InventorySessionEditor, UserGroup.InventorySessionAdmin],
    },
  },
  {
    path: 'kardex-audit',
    component: KardexAuditDashboardComponent,
    canActivate: [AuthenticationGuard, RoleGuard],
    data: {
      breadcrumb: [{ label: 'KardexAudit_Title' }],
      roles: [UserGroup.KardexAuditViewer, UserGroup.KardexAuditAdmin],
    },
  },
  {
    path: 'business-lot-numbers',
    component: BusinessLotNumbersComponent,
    canActivate: [AuthenticationGuard, RoleGuard],
    data: {
      breadcrumb: [{ label: 'Business_Lot_Numbers_Title' }],
      roles: [UserGroup.BusinessLotNumbersViewer, UserGroup.BusinessLotNumbersAdmin],
    },
  },
  {
    path: '',
    pathMatch: 'full',
    canActivate: [AuthenticationGuard, defaultRouteGuard(MENU_ITEMS, 'Inventory')],
    component: UnauthorizedComponent,
  },
  {
    path: '**',
    redirectTo: '',
  },
];

@NgModule({
  imports: [RouterModule.forRoot(routes)],
  exports: [RouterModule],
})
export class AppRoutingModule {}
